import type { AcmColumn, AcmRecord } from '../types/kpi.js';

/** Excel 文字化け回避用の UTF-8 BOM。 @graph-connects none */
const UTF8_BOM = '\uFEFF';

/** CSV セル1個をエスケープする（カンマ/引用符/改行を含む場合は二重引用符で囲む）。 @graph-connects none */
export function escapeCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business acm_records の列定義＋行から CSV 文字列を生成する。ヘッダは日本語ラベル、Excel 文字化け回避のため先頭に UTF-8 BOM を付ける。
 * @graph-connects none
 */
export function buildCsv(columns: AcmColumn[], rows: AcmRecord[]): string {
  const header = ['日付', ...columns.map((col) => col.label)];
  const lines = rows.map((row) => [row.date, ...columns.map((col) => String(row[col.key]))]);
  const body = [header, ...lines].map((cells) => cells.map(escapeCell).join(',')).join('\r\n');
  return `${UTF8_BOM}${body}`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business テキストコンテンツを指定 MIME type の Blob としてブラウザダウンロードさせる汎用関数。Blob URL → アンカークリック → URL 解放。
 * @graph-connects none
 */
export function downloadFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 生成済み CSV 文字列をブラウザでダウンロードさせる。
 * @graph-connects downloadFile [calls] 汎用ファイルダウンロード
 */
export function downloadCsv(filename: string, csv: string): void {
  downloadFile(filename, csv, 'text/csv;charset=utf-8;');
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business SQL テキストを .sql ファイルとしてブラウザダウンロードさせる。
 * @graph-connects downloadFile [calls] 汎用ファイルダウンロード
 */
export function downloadSql(filename: string, sql: string): void {
  downloadFile(filename, sql, 'application/sql;charset=utf-8;');
}
